import React, { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { TopTitle } from '../components/atoms/TopTitle'
import { PageTitle } from '../components/atoms/PageTitle'
import { callAxios, useAxios } from '../hooks/useAxios'
import { ApplianceData } from '../classes/appliances/Appliance'
import { useApp } from '../hooks/useApp'
import { Box } from '@mui/material'
import { useNoRefresh } from '../hooks/useRefresh'
import { ProfitHistory } from '../components/ProfitHistory'
import { useAppliancePorts } from '../hooks/useAppliancePorts'
import { PageText } from '../components/atoms/PageText'
import { ReserveFeeTable } from '../components/ReserveFeeTable'
import { ReserveStatements } from '../components/ReserveStatements'
import { ReserveFeeSummary } from '../types/charts'

export function ProfitsPage(): React.ReactElement {
  const { t } = useTranslation()
  const [appliances, setAppliances] = useState<ApplianceData[]>([])
  // Battery ports whose profit data carries a solar value; the solar note is shown for any.
  const [pvPorts, setPvPorts] = useState<Record<string, boolean>>({})
  // Battery ports showing the view after the service fee; any of them brings up the sum table.
  const [feePorts, setFeePorts] = useState<Record<string, boolean>>({})
  const [feeSummary, setFeeSummary] = useState<ReserveFeeSummary | null>(null)
  const feeMode = Object.values(feePorts).some(Boolean)
  const app = useApp()
  useNoRefresh()
  const { items, syncing } = useAppliancePorts(appliances)

  // Show only first for now. Later this could be selected using the `page` concept.
  const location = app.locations.length ? app.locations[0] : null

  useAxios<ApplianceData[]>({
    url: `/appliances?location=${location && location.id}`,
    onSuccess: (data) => {
      setAppliances(
        data.filter((a) => a.type === 'FCR_BATTERY' || a.type === 'BATTERY')
      )
    }
  })

  useEffect(() => {
    if (!feeMode || feeSummary) return

    callAxios<ReserveFeeSummary>({ url: '/reserve-fees' }).then((res) => {
      if (res.success) setFeeSummary(res.data)
    })
  }, [feeMode, feeSummary])

  return (
    <>
      <TopTitle title={t('Reserve Market Profits')} />
      <Box sx={{ mb: 3 }}>
        <Trans>
          The compensations shown are preliminary and may change slightly as the
          credits are rolled out to customers. Monthly fee will be deducted.
        </Trans>
      </Box>
      {items.map(({ appliance, portControl }) => (
        <Box key={appliance.id} sx={{ mb: 6 }}>
          <PageTitle
            sx={{ mb: 2 }}
            title={`${appliance.name} - ${t(`Appliance-${appliance.type}`)}`}
          />
          {portControl
            ? <ProfitHistory
                device={portControl.device}
                port={portControl.port}
                onPvValue={(hasPv) => setPvPorts((prev) =>
                  prev[String(portControl.port.id)] === hasPv
                    ? prev
                    : { ...prev, [String(portControl.port.id)]: hasPv })}
                fcr={appliance.type === 'FCR_BATTERY'}
                onFeeMode={(on) => setFeePorts((prev) => ({ ...prev, [String(portControl.port.id)]: on }))}
              />
            : !syncing && <PageText>{t('The port of the appliance was not found. Please check your devices.')}</PageText>}
        </Box>
      ))}
      {feeMode && feeSummary && feeSummary.devices > 1 && (
        <ReserveFeeTable
          sx={{ mb: 6 }}
          total
          rows={feeSummary.rows}
          vatRegistered={feeSummary.vat_registered}
        />
      )}
      {feeMode && <ReserveStatements sx={{ mb: 6 }}/>}
      <Box sx={{ mb: 3 }}>
        <Trans>SELF_SAVING_USER_NOTES</Trans>
      </Box>
      {Object.values(pvPorts).some(Boolean) && (
        <Box sx={{ mb: 3 }}>
          <Trans>PV_VALUE_USER_NOTES</Trans>
        </Box>
      )}
    </>
  )
}
